import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faListDropdown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/sharp-light-svg-icons";
import { Menu } from "@headlessui/react";
import {
    type ColumnDef,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { CompactTableUI } from "../components/DataTable";
import { SecondaryButton } from "./Buttons";

// TFilters is the generated list-request type of whatever is being bulk-edited:
// the filters are spread straight into API calls, so typing them here is what
// catches a filter the endpoint doesn't accept.
export interface BulkActionsProps<TFilters = Record<string, unknown>> {
    filters: TFilters;
    selectedIds: string[];
    allSelected: boolean;
    onFinalizeAction: () => void;
}

export interface BulkActionsConfirmationProps<
    TFilters = Record<string, unknown>,
> extends BulkActionsProps<TFilters> {
    visible: boolean;
    onClose: () => void;
}

// Every bulk endpoint answers with this shape. The generated client declares
// one interface per endpoint (BulkEventsResponse, BulkEventsArchiveResponse,
// BulkUpdateUnmatchedDataPoints, …), all structurally identical to this one and
// all typing failures as bare dicts.
export interface BulkActionResult {
    ok: number;
    failed: number;
    failures: Record<string, unknown>[];
    failuresTruncated: boolean;
}

// A failure entry, once narrowed from the bare dict the generated client hands
// us. Normalizing here is what keeps `as BulkFailure[]` out of the call sites.
export interface BulkFailure {
    id: string;
    errors: Record<string, unknown>;
}

const toBulkFailures = (
    failures: BulkActionResult["failures"],
): BulkFailure[] =>
    failures.map((failure) => ({
        id: String(failure.id ?? ""),
        errors: (failure.errors ?? {}) as Record<string, unknown>,
    }));

// Builds the link back to the record a failure refers to. Used both by the
// failures table and by the downloaded CSV, so each feature decides where its
// records live.
export type BulkFailureLinkBuilder = (id: string) => string;

// Map serializer field names to friendlier labels shown in UI/CSV.
const FIELD_LABELS: Record<string, string> = {
    event_status: "Status change",
    responsible: "Assignee",
    main_event: "Linked event",
    form_data: "Form data",
    non_field_errors: "",
};

const labelForField = (key: string): string =>
    key in FIELD_LABELS ? FIELD_LABELS[key] : key;

// Flattens a DRF serializer .errors dict into a single human-readable line.
// Handles nested dicts and lists. Example:
//   {event_status: ["Not allowed"], root_cause: {code: ["Required"]}}
//   -> "Status change: Not allowed | root_cause.code: Required"
const formatErrors = (errors: unknown, prefix = ""): string => {
    if (errors == null) return "";
    if (typeof errors === "string") {
        return prefix ? `${prefix}: ${errors}` : errors;
    }
    if (Array.isArray(errors)) {
        return errors
            .map((e) => formatErrors(e, prefix))
            .filter(Boolean)
            .join("; ");
    }
    if (typeof errors === "object") {
        return Object.entries(errors as Record<string, unknown>)
            .map(([key, value]) => {
                const label = prefix ? `${prefix}.${key}` : labelForField(key);
                return formatErrors(value, label);
            })
            .filter(Boolean)
            .join(" | ");
    }
    const str = String(errors);
    return prefix ? `${prefix}: ${str}` : str;
};

const escapeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const downloadFailuresCsv = (
    failures: BulkFailure[],
    filenamePrefix: string,
    linkForId: BulkFailureLinkBuilder,
) => {
    const header = "Record link,Error reason";
    const rows = failures.map((f) => {
        // Absolute, so the link still works pasted into a spreadsheet.
        const link = new URL(linkForId(f.id), window.location.origin).href;
        const reason = formatErrors(f.errors) || "Unknown error";
        return `${escapeCsvCell(link)},${escapeCsvCell(reason)}`;
    });
    const csv = [header, ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    a.download = `${filenamePrefix}-failures-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const BulkFailuresTruncatedNote = ({
    result,
}: {
    result: BulkActionResult;
}) => {
    if (!result.failuresTruncated || !result.failures.length) return null;
    return (
        <p className="text-xs text-gray-500 mb-1">
            Only the first {result.failures.length} failures are included in the
            CSV.
        </p>
    );
};

const failureColumns = (
    linkForId: BulkFailureLinkBuilder,
): ColumnDef<BulkFailure, any>[] => [
    {
        accessorKey: "id",
        header: "ID",
        cell: ({ getValue }) => {
            const id = getValue<string>();
            return (
                <a
                    href={linkForId(id)}
                    target="_blank"
                    rel="noopener"
                    className="text-blue-600 hover:underline font-mono text-xs"
                >
                    {id}
                </a>
            );
        },
    },
    {
        accessorKey: "errors",
        header: "Errors",
        cell: ({ getValue }) => {
            const errors = getValue<Record<string, unknown>>();
            return (
                <ul className="text-xs space-y-0.5">
                    {Object.entries(errors).map(([field, messages]) => {
                        const items = Array.isArray(messages)
                            ? messages
                            : [messages];
                        return items.map((msg, i) => (
                            <li key={`${field}-${i}`}>
                                <span className="font-semibold capitalize">
                                    {field.replaceAll("_", " ")}
                                </span>
                                {": "}
                                {String(msg)}
                            </li>
                        ));
                    })}
                </ul>
            );
        },
    },
];

export const BulkFailuresPreview = ({
    result,
    linkForId,
}: {
    result: BulkActionResult;
    linkForId: BulkFailureLinkBuilder;
}) => {
    const columns = useMemo(() => failureColumns(linkForId), [linkForId]);
    const failures = useMemo(
        () => toBulkFailures(result.failures),
        [result.failures],
    );
    const table = useReactTable({
        data: failures,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        autoResetPageIndex: false,
    });

    return (
        <div className="h-44 border rounded overflow-hidden">
            <BulkFailuresTruncatedNote result={result} />
            <CompactTableUI
                table={table}
                dataName="failures"
                columns={columns}
                loadingData={false}
                itemCount={failures.length}
            />
        </div>
    );
};

export const BulkFailuresDownloadButton = ({
    result,
    filenamePrefix,
    linkForId,
}: {
    result: BulkActionResult;
    filenamePrefix: string;
    linkForId: BulkFailureLinkBuilder;
}) => {
    if (result.failures.length === 0) return null;
    return (
        <SecondaryButton
            type="button"
            onClick={() =>
                downloadFailuresCsv(
                    toBulkFailures(result.failures),
                    filenamePrefix,
                    linkForId,
                )
            }
        >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Download failures as CSV
        </SecondaryButton>
    );
};

export const BulkMenuItem = ({
    icon,
    label,
    enabled,
    disabledTitle,
    onClick,
}: {
    icon: IconDefinition;
    label: string;
    enabled: boolean;
    disabledTitle: string;
    onClick: () => void;
}) => (
    <Menu.Item
        as="button"
        disabled={!enabled}
        onClick={onClick}
        title={enabled ? undefined : disabledTitle}
        className={`rounded p-1 flex items-center gap-1 text-left ${
            enabled
                ? "hover:bg-ae-slate-200"
                : "text-gray-400 cursor-not-allowed"
        }`}
    >
        <FontAwesomeIcon icon={icon} />
        {label}
    </Menu.Item>
);

// "Bulk Actions" dropdown trigger + panel. Children should be BulkMenuItems.
export const BulkActionsMenu = ({
    disabled,
    children,
}: {
    disabled: boolean;
    children: React.ReactNode;
}) => (
    <Menu as="div" className="relative">
        <Menu.Button
            disabled={disabled}
            title={
                disabled
                    ? "Select rows on the table to enable bulk actions"
                    : undefined
            }
            className={`
                h-7 rounded flex items-center justify-between px-4 border gap-2
                ${disabled ? "bg-neutral-300" : "bg-ae-blue-550"} text-white
            `}
        >
            <FontAwesomeIcon icon={faListDropdown} />
            Bulk Actions
        </Menu.Button>
        <Menu.Items className="absolute bg-white shadow-lg rounded border-2 p-1 flex flex-col gap-1 mt-0.5 whitespace-nowrap z-50">
            {children}
        </Menu.Items>
    </Menu>
);
